import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, TextField, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { fetchCoupons, createCoupons, deleteCoupons } from '../api/couponsAPI';
import { toast } from 'react-toastify';
import moment from 'moment';

const CouponManager = () => {
    const [coupons, setCoupons] = useState([]);
    const [newCoupon, setNewCoupon] = useState({
        name: '',
        expiry: '',
        discount: ''
    });
    const [isLoading, setIsLoading] = useState(false); // Loader for fetching data
    const [isActionLoading, setIsActionLoading] = useState(false); // Loader for create/delete actions
    const [selectedCouponId, setSelectedCouponId] = useState(null); // To store the coupon ID for deletion
    const [isDialogOpen, setIsDialogOpen] = useState(false); // State for delete confirmation dialog

    // Fetch coupons (GET)
    const fetchAllCoupons = () => {
        setIsLoading(true);
        fetchCoupons()
            .then(data => setCoupons(data))
            .catch(error => {
                console.error('Error fetching coupons:', error)
                toast.error('Failed to fetch coupons');

            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        fetchAllCoupons();
    }, []);

    // Handle input change for new coupon
    const handleChange = (e) => {
        const { name, value } = e.target;
        setNewCoupon(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    // Add a new coupon (POST)
    const handleCreateCoupon = () => {
        setIsActionLoading(true);
        
        createCoupons(newCoupon)
            .then(() => {
                setNewCoupon({ name: '', expiry: '', discount: '' });
                fetchAllCoupons(); 
                toast.success('Coupon created successfully');
            })
            .catch(error =>{
                console.error('Error creating coupon:', error)
                toast.error('Failed to create coupon');
            })
            .finally(() => setIsActionLoading(false));
    };

    // Open confirmation dialog for deleting a coupon
    const handleDeleteDialogOpen = (id) => {
        setSelectedCouponId(id);
        setIsDialogOpen(true);
    };

    // Close confirmation dialog
    const handleDeleteDialogClose = () => {
        setIsDialogOpen(false);
        setSelectedCouponId(null);
    };

    // Confirm and delete the coupon (DELETE)
    const handleDeleteCoupon = () => {
        setIsActionLoading(true);
        deleteCoupons(selectedCouponId)
            .then(() => {
                fetchAllCoupons(); 
                toast.success('Coupon deleted successfully');
            })
            .catch(error => {
                console.error('Error deleting coupon:', error);
                toast.error('Failed to delete coupon');
            })
            .finally(() => {
                setIsActionLoading(false);
                handleDeleteDialogClose();
            });
    };

    return (
        <div>
            <h2>Coupon Manager</h2>

            {/* Create Coupon Form */}
            <div style={{ marginBottom: '20px' }}>
                <TextField
                    label="Name"
                    name="name"
                    value={newCoupon.name}
                    onChange={handleChange}
                    style={{ marginRight: '10px' }}
                />
                <TextField
                    label="Expiry Date"
                    name="expiry"
                    value={newCoupon.expiry}
                    onChange={handleChange}
                    type="date"  // Native HTML date picker
                    InputLabelProps={{
                        shrink: true,
                    }}
                    style={{ marginRight: '10px' }}
                />
                <TextField
                    label="Discount"
                    name="discount"
                    value={newCoupon.discount}
                    onChange={handleChange}
                    type="number"
                    style={{ marginRight: '10px' }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateCoupon}
                    disabled={isActionLoading} // Disable button during loading
                >
                    {isActionLoading ? <CircularProgress size={24} /> : 'Create Coupon'}
                </Button>
            </div>

            {/* Loading Spinner when fetching data */}
            {isLoading ? (
                <CircularProgress />
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Expiry</TableCell>
                                <TableCell>Discount</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {coupons.map(coupon => (
                                <TableRow key={coupon._id}>
                                    <TableCell>{coupon.name}</TableCell>
                                    <TableCell>{moment(coupon.expiry).format('DD-MM-YYYY')}</TableCell>
                                    <TableCell>{coupon.discount}%</TableCell>
                                    <TableCell>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => handleDeleteDialogOpen(coupon._id)}
                                            disabled={isActionLoading} // Disable button during loading
                                        >
                                            {isActionLoading ? <CircularProgress size={24} /> : 'Delete'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={isDialogOpen}
                onClose={handleDeleteDialogClose}
            >
                <DialogTitle>{"Confirm Delete"}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this coupon?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteCoupon} color="secondary" disabled={isActionLoading}>
                        {isActionLoading ? <CircularProgress size={24} /> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CouponManager;
